import { useDispatch, useSelector } from 'react-redux';
import { SharedMemberInfoModalUI } from './SharedMemberInfoModalUI';
import { selectTeamMembersHash } from 'selectors';
import { TeamMembershipType } from 'types';
import { ComponentProps, FC, useCallback } from 'react';
import { updateTeamMember } from 'thunk';
import { usePromoteSuperAdminModal } from 'TeamModule/hooks/usePromoteSuperAdminModal';
import useSetUsersTemporaryPasswordModal from '../SetUsersTemporaryPasswordModal/useSetUsersTemporaryPasswordModal';
import { useRequestStatus } from 'RequestStatusModule/hooks/useRequestStatus';
import { useFeatureFlags, getIsEditingMemberEmailDisabledWithContext } from 'hooks/useFeatureFlags';

export const TeamMemberInfoModal: FC<{
  accountId: TeamMembershipType['account_id'];
  teamId: number;
  onClose: VoidFunction;
}> = ({ accountId, teamId, onClose }) => {
  const memberHash = useSelector(selectTeamMembersHash);
  const member = memberHash[accountId];
  const dispatch = useDispatch();
  const teamMembershipId = member?.team_membership_id;
  const requestStatusId = `update-team-member-${teamMembershipId}`;
  const { openPromoteSuperAdminModal } = usePromoteSuperAdminModal();
  const { openSetUsersTemporaryPasswordModal } = useSetUsersTemporaryPasswordModal();
  const { requestStatus: updateTeamMembershipsRequestStatus } = useRequestStatus({
    requestStatusId,
  });
  const isRequesting = Boolean(updateTeamMembershipsRequestStatus?.isRequesting);
  const updateMemberError = updateTeamMembershipsRequestStatus?.error?.[0];

  const onFormSubmit = useCallback<ComponentProps<typeof SharedMemberInfoModalUI>['onFormSubmit']>(
    (values) => {
      // TODO: Fix with Redux Toolkit configureStore migration
      dispatch(
        updateTeamMember({
          ...values,
          requestStatusId,
          teamMembershipId: teamMembershipId,
        }) as any,
      );
    },
    [dispatch, teamMembershipId, requestStatusId],
  );

  const popoverItems = [
    {
      label: 'Promote',
      onClick: () => {
        openPromoteSuperAdminModal({ accountId });
      },
    },
    {
      label: 'Set Temporary Password',
      onClick: () => {
        openSetUsersTemporaryPasswordModal({
          accountIds: [accountId],
          teamId,
        });
      },
    },
  ];

  // Spec: Disabled editing email when in Mosaic Sandbox team on Try tenant
  const { context: featureFlagContext } = useFeatureFlags();
  const isEditMemberEmailDisabled = getIsEditingMemberEmailDisabledWithContext({
    ...featureFlagContext,
    teamId,
  });

  // `memberHash` only holds the current page (GLX-1740); if this member isn't
  // resolvable, render nothing rather than crashing on member.* access.
  if (!member) {
    return null;
  }

  return (
    <SharedMemberInfoModalUI
      onClose={onClose}
      firstName={member.first_name}
      lastName={member.last_name}
      email={member.email}
      accountId={member.account_id}
      isEditMemberEmailDisabled={isEditMemberEmailDisabled}
      popoverItems={popoverItems}
      onFormSubmit={onFormSubmit}
      isRequesting={isRequesting}
      error={updateMemberError}
    />
  );
};
